/**
 * Eén bron van waarheid voor het aantal stuks op een Printful-regel.
 *
 * De productconfigurator zet het gekozen aantal in de productoptie
 * `quantity` (`configuration.options.quantity`), niet in de regelhoeveelheid
 * van de winkelwagen. Oudere winkelwagens hebben daardoor regelhoeveelheid 1
 * met bijvoorbeeld `options.quantity = 5`. Werd alleen de regelhoeveelheid
 * gelezen, dan rekende de kassa 1 stuk af, schreef 1 stuk op de orderregel en
 * ging er 1 stuk naar Printful, terwijl de klant er 5 had gekozen.
 *
 * Regel: een geldige optie-hoeveelheid is leidend, anders de
 * regelhoeveelheid, anders 1. Nooit vermenigvuldigen: optie 5 en regel 5 is 5
 * stuks, geen 25.
 */

/** Een geldig, positief heel aantal stuks, of null. Accepteert ook "5". */
export function parsePrintfulQuantity(value: unknown): number | null {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return Math.floor(parsed);
}

export function resolvePrintfulLineQuantity(options: unknown, lineQuantity?: unknown): number {
  const optionQuantity = options && typeof options === 'object' && !Array.isArray(options)
    ? parsePrintfulQuantity((options as Record<string, unknown>).quantity)
    : null;
  return optionQuantity ?? parsePrintfulQuantity(lineQuantity) ?? 1;
}

type PrintfulCartLine = {
  quantity?: unknown;
  configuration?: { quantity?: unknown; options?: unknown; displayOptions?: unknown; [key: string]: unknown } | null;
  [key: string]: unknown;
};

/**
 * Zet regelhoeveelheid, `configuration.quantity` en (als de regel die optie
 * draagt) `options.quantity` op hetzelfde aantal, zodat prijs, orderregel en
 * de Printful-order daarna allemaal hetzelfde getal lezen.
 */
export function normalizePrintfulCartLine<T extends PrintfulCartLine>(item: T): T {
  const configuration = item.configuration && typeof item.configuration === 'object' ? item.configuration : {};
  const options = configuration.options && typeof configuration.options === 'object' && !Array.isArray(configuration.options)
    ? configuration.options as Record<string, unknown>
    : null;
  const quantity = resolvePrintfulLineQuantity(
    options,
    parsePrintfulQuantity(configuration.quantity) ?? item.quantity,
  );
  const displayOptions = Array.isArray(configuration.displayOptions)
    ? syncQuantityDisplayRows(configuration.displayOptions, quantity)
    : undefined;
  return {
    ...item,
    quantity,
    configuration: {
      ...configuration,
      quantity,
      ...(options ? { options: options.quantity !== undefined ? { ...options, quantity } : options } : {}),
      ...(displayOptions ? { displayOptions } : {}),
    },
  };
}

/**
 * De displayOptions-regel "Aantal" is een snapshot van de configurator. Werd
 * het aantal daarna in de winkelwagen aangepast, dan bleef daar "1 stuks"
 * staan terwijl de regel 2 stuks bestelde. Het getal in die regel volgt het
 * genormaliseerde aantal; de woorden ("stuks", "pieces") blijven staan.
 */
export function syncQuantityDisplayRows(rows: unknown[], quantity: number): unknown[] {
  return rows.map((row) => {
    if (!row || typeof row !== 'object' || (row as { key?: unknown }).key !== 'quantity') return row;
    const value = (row as { value?: unknown }).value;
    const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
    const next = /\d/.test(text) ? text.replace(/\d[\d.,\s]*/, (match) => `${quantity}${/\s$/.test(match) ? ' ' : ''}`) : String(quantity);
    return next === value ? row : { ...(row as object), value: next };
  });
}
